import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, concatMap, map, tap, timeoutWith } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  ActionsOperators,
  AllPrimaryEntitiesNames,
  AllPrimaryEntitiesTypes,
  BullhornFieldType,
  ChangedField,
  Entities,
  IAwesomeDocsWorkflowConfig,
  IDataRulesTasks,
  IPromptProfile,
  IRuleStats,
  ITask,
  RuleData,
  Samples,
  TaskState,
  UpdateSamples
} from './data-rules-model';

@Injectable({
  providedIn: 'root'
})
export class DataRulesService {

  public actionOperators = ActionsOperators;
  private credentialingEntities: string[] = [
    'CertificationRequirement',
    'CertificationFileAttachment',
    'CandidateCertification'
  ];

  constructor(private http: HttpClient) {
  }

  static isSelectedEntitySearchEntity(selectedEntity: (Entities | AllPrimaryEntitiesNames)): boolean {
    const searchEntities: AllPrimaryEntitiesNames[] = [AllPrimaryEntitiesTypes.Candidate, AllPrimaryEntitiesTypes.Note];
    return searchEntities.includes(selectedEntity);
  }

  bullhornRules(rules: RuleData[]): RuleData[] {
    rules = rules.filter(rule => rule.type === 'BullhornBullhornUpdate');
    this.convertDatesFromISOFormat(rules);
    console.log('KPDR: Rules (BH2BH only)', rules.length);
    return rules;
  }

  getRuleSamples(ruleID): Observable<Samples> {
    return this.http.get<Samples>(`${environment.dataRulesUrl}/rule/${ruleID}/sample`).pipe(
      timeoutWith(60000, throwError(new HttpErrorResponse({error: new Error('Sample timeout'), status: 0}))),
      tap((ruleSamples: Samples) => {
        ruleSamples.updateSamples.forEach((updateSample: UpdateSamples) => {
          const addressChanges: ChangedField[] = [];
          updateSample.changedFields.forEach((field: ChangedField) => {
            if (field.fieldType === BullhornFieldType.ADDRESS) {
              let oldAddress = field.oldValue;
              let newAddress = field.newValue;

              // this is for paired entities
              if (typeof oldAddress === 'object' && oldAddress?.hasOwnProperty('value')) {
                oldAddress = oldAddress.value;
              }
              if (typeof newAddress === 'object' && newAddress.hasOwnProperty('value')) {
                newAddress = newAddress.value;
              }

              if (oldAddress?.address1 !== newAddress.address1) {
                addressChanges.push(new ChangedField(`${field.fieldName}.address1`,
                  BullhornFieldType.STRING,
                  oldAddress?.address1,
                  newAddress.address1));
              }

              if (oldAddress?.address2 !== newAddress.address2) {
                addressChanges.push(new ChangedField(`${field.fieldName}.address2`,
                  BullhornFieldType.STRING,
                  oldAddress?.address2,
                  newAddress.address2));
              }

              if (oldAddress?.city !== newAddress.city) {
                addressChanges.push(new ChangedField(`${field.fieldName}.city`,
                  BullhornFieldType.STRING,
                  oldAddress?.city,
                  newAddress.city));
              }

              if (oldAddress?.state !== newAddress.state) {
                addressChanges.push(new ChangedField(`${field.fieldName}.state`,
                  BullhornFieldType.STRING,
                  oldAddress?.state,
                  newAddress.state));
              }

              if (oldAddress?.zip !== newAddress.zip) {
                addressChanges.push(new ChangedField(`${field.fieldName}.zip`,
                  BullhornFieldType.STRING,
                  oldAddress?.zip,
                  newAddress.zip));
              }

              if (oldAddress?.countryID !== newAddress.countryID) {
                addressChanges.push(new ChangedField(`${field.fieldName}.country`,
                  BullhornFieldType.STRING,
                  oldAddress?.countryID,
                  newAddress.countryID));
              }
            }
          });

          updateSample.changedFields = updateSample.changedFields.filter(field => field.fieldType !== BullhornFieldType.ADDRESS);
          updateSample.changedFields = updateSample.changedFields.concat(addressChanges);
        });
        console.log('KPDR: Rule Samples', ruleSamples);
      })
    );
  }

  getRule(id: string): Observable<RuleData> {
    return this.http.get<RuleData>(`${environment.dataRulesUrl}/rule/${id}`).pipe(
      map(rule => this.convertDatesFromISOFormat([rule])[0]),
      tap(rule => console.log('KPDR: Rule', rule))
    );
  }

  // FIXME: not used outside of tests?
  getRules(ids: string[]): Observable<RuleData[]> {
    if (ids.length < 1) {
      return of([]);
    }
    const idsString = ids.reduce((acc, id, i, arr) => {
      return `${acc}${id}${i < arr.length - 1 ? ',' : ''}`;
    }, '');
    return this.http.get<RuleData[]>(`${environment.dataRulesUrl}/rule`, {params: {ids: idsString}}).pipe(
      map(rules => {
        return this.convertDatesFromISOFormat(rules);
      })
    );
  }

  applyToBullhorn(task: ITask): Observable<any> {
    return this.http.post<any>(`${environment.dataRulesUrl}/task`, task);
  }

  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${environment.dataRulesUrl}/rule/${id}`);
  }

  getAllRules(): Observable<RuleData[]> {
    console.time('getAllRules');
    // todo ass addition to existing data handle - do a setting the data to service storage (KDATA-1647)
    return this.http.get<RuleData[]>(`${environment.dataRulesUrl}/rule`).pipe(
      tap(allRules => console.timeEnd('getAllRules')),
      tap(allRules => console.log('KPDR: Rules', allRules.length))
    );
  }

  revertTask(id: string, sourceRuleId: string): Observable<IDataRulesTasks> {
    const rollback$ = this.http.get<IDataRulesTasks>(`${environment.dataRulesUrl}/task/${id}/rollback`);
    return rollback$.pipe(
      map(rollbackTask => {
        rollbackTask.state = TaskState[rollbackTask.state];   // convert string to enum
        return rollbackTask;
      }),
      concatMap(rollbackTask =>
        this.getRule(sourceRuleId).pipe(
          map(rule => {
            return {
              ...rollbackTask,
              name: 'Revert: ' + rule.name,
              user: rollbackTask.userId,
              ruleDeleted: false,
              isRollbackTask: true
            };
          }), catchError(ruleError => {
            if (ruleError.status !== 404)
              throw ruleError;
            return of({
              ...rollbackTask,
              name: `Revert: Rule ${sourceRuleId} (deleted)`,
              entity: '-',
              entityLabel: '-',
              user: rollbackTask.userId,
              ruleDeleted: true,
              isRollbackTask: true
            });
          }))
      )
    );
  }

  stopTask(task: IDataRulesTasks): Observable<any> {
    return this.http.get(`${environment.dataRulesUrl}/task/${task.sortKey}/pause`);
  }

  resumeTask(task: IDataRulesTasks): Observable<any> {
    return this.http.get(`${environment.dataRulesUrl}/task/${task.sortKey}/resume`);
  }

  cancelTask(task: IDataRulesTasks): Observable<any> {
    return this.http.get(`${environment.dataRulesUrl}/task/${task.sortKey}/cancel`);
  }

  getTaskPage(ruleId: string, pageSize: number, lastKey?: string): Observable<{moreResults: boolean, tasks: IDataRulesTasks[]}> {
    let url = `${environment.dataRulesUrl}/task/page/${ruleId}?num=${pageSize}&forward=false`;
    if (lastKey) {
      url += `&lastKey=${lastKey}`;
    }

    const rules$ = this.getAllRules();
    const res$ = this.http.get<{moreResults: boolean, tasks: IDataRulesTasks[]}>(url);
    const result$ = forkJoin({rules: rules$, res: res$});

    return result$.pipe(
      map(({rules, res}) => {

      console.log('rules!', rules);
        res.tasks = res.tasks.map(t => {
          t.state = TaskState[t.state];   // convert string to enum
          t.sourceRuleId = t.ruleId;

          const rule: any = rules.find(r => ('' + r.id) === t.ruleId);

          if (!rule) {
            return {...t, name: `Deleted: ${t.ruleId}`, user: t.userId, userName: t.userName, ruleDeleted: true};
          }

          const isRollbackTask = rule.type === 'BullhornBullhornRollbackUpdate';
          let rollbackString = '';
          if (isRollbackTask) {
            rollbackString = 'Revert: ';
          }

          const ruleName = rollbackString + rule.name;
          return {...t,
            name: ruleName,
            user: t.userId,
            userName: t.userName,
            isRollbackTask: isRollbackTask};
        });

        console.log('KPDR: Tasks', res.tasks.length);

        return { moreResults: res.moreResults, tasks: res.tasks };
      })
    );
  }

  private convertDatesFromISOFormat(rules: RuleData[]): RuleData[] {
    rules.forEach(rule => {
      rule.action.config.steps = rule.action.config.steps.map(step => {
        if (step.type === 'REPLACE' && step.destinationType === 'DATE')
          step.value = this.removeTimeInfo(step.value);
        return step;
      });

      rule.source.config.criteria = rule.source.config.criteria.map(criteria => {
        if (criteria.fieldType === 'DATE' && (criteria.operator !== 'within' && criteria.operator !== 'notWithin'))
          criteria.value = this.removeTimeInfo(criteria.value);
        return criteria;
      });
    });

    return rules;
  }

  private convertDatesToUTCFormat(rule: RuleData): void {
    rule.action.config.steps = rule.action.config.steps.map(step => {
      if (step.type === 'REPLACE' && step.destinationType === 'DATE') {
        step.value = new Date(step.value).toISOString().slice(0, -1) + 'UTC';
      }
      return step;
    });

    rule.source.config.criteria = rule.source.config.criteria.map(criteria => {
      if (criteria.fieldType === 'DATE' && (criteria.operator !== 'within' && criteria.operator !== 'notWithin'))
        criteria.value = new Date(criteria.value).toISOString();
      return criteria;
    });
  }

  private removeTimeInfo(datetimeISO: string): string {
    const tIndex = datetimeISO.indexOf('T');
    return tIndex > -1 ? datetimeISO.substr(0, tIndex) : datetimeISO;
  }

  get credentialingEntitiesAllowed() {
    return this.credentialingEntities;
  }

  checkAuth() {
    return this.http.get(`${environment.dataRulesUrl}/auth/checkAuth`);
  }

  checkAdminUser() {
    return this.http.get(`${environment.dataRulesUrl}/auth/checkAdminUser`);
  }

  getRuleStats(): Observable<IRuleStats[]> {
    return this.http.get<IRuleStats[]>(`${environment.dataRulesUrl}/stats`);
  }

  getAiPrompts(entity: string): Observable<IPromptProfile[]> {
    return this.http.get<IPromptProfile[]>(`${environment.dataRulesUrl}/ai/prompts/${entity}`);
  }

  getAwesomeDocsConfigs(entity: Entities): Observable<IAwesomeDocsWorkflowConfig[]> {
    return this.http.get<IAwesomeDocsWorkflowConfig[]>(`${environment.dataRulesUrl}/awesomedocs/configs/${entity}`);
  }

  actionOpName(operator: ActionsOperators): string {
    switch (operator) {
      case ActionsOperators.COPY:
        return 'COPY';
      case ActionsOperators.MOVE:
        return 'MOVE';
      case ActionsOperators.NULLIFY:
        return 'NULLIFY';
      case ActionsOperators.REPLACE:
        return 'REPLACE';
      case ActionsOperators.REPLACE_RELATIVE:
        return 'REPLACE_RELATIVE';
      case ActionsOperators.SWAP:
        return 'SWAP';
      case ActionsOperators.ADD:
        return 'ADD';
      case ActionsOperators.REMOVE:
        return 'REMOVE';
      case ActionsOperators.CHANGE:
        return 'CHANGE';
      case ActionsOperators.APPEND:
        return 'APPEND';
      case ActionsOperators.FORMAT:
        return 'FORMAT';
      case ActionsOperators.SUBSTITUTE:
        return 'SUBSTITUTE';
      case ActionsOperators.PREPEND:
        return 'PREPEND';
      case ActionsOperators.DELETE:
        return 'DELETE';
      case ActionsOperators.INSERT:
        return 'INSERT';
      case ActionsOperators.MATHS:
        return 'MATHS';
      case ActionsOperators.APPEND_ENTRY:
        return 'APPEND_ENTRY';
      case ActionsOperators.COUNT:
        return 'COUNT';
      case ActionsOperators.INSERT_MULTIPLE:
        return 'INSERT_MULTIPLE';
      case ActionsOperators.CONCATENATE:
        return 'CONCATENATE';
      case ActionsOperators.AIASSIST:
        return 'AIASSIST';
      case ActionsOperators.AIASSIST_NOTE:
        return 'AIASSIST_NOTE';
      case ActionsOperators.AWESOMEDOCS:
        return 'AWESOMEDOCS';
      case ActionsOperators.ENRICH:
        return 'ENRICH';
      case ActionsOperators.PARSE_RESUME:
        return 'PARSE_RESUME';
    }
  }
}
